import { currentLocale } from '@/i18n';

export function parseDateOnly(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    throw new RangeError(`Invalid date-only value: ${value}`);
  }

  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  // Date's multi-argument constructor remaps years 0-99 to 1900-1999.
  const parsed = new Date(2000, 0, 1);
  parsed.setHours(0, 0, 0, 0);
  parsed.setFullYear(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    throw new RangeError(`Invalid Gregorian date: ${value}`);
  }
  return parsed;
}

export function tryParseDateOnly(value: string): Date | null {
  try {
    return parseDateOnly(value);
  } catch {
    return null;
  }
}

export function addDays(date: Date, days: number): Date {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

export function formatDateOnlyMonthDay(value: string): string {
  const date = tryParseDateOnly(value);
  if (!date) {
    return "";
  }
  return new Intl.DateTimeFormat(currentLocale(), { month: 'short', day: 'numeric' }).format(date);
}

/** Parse SQLite's CURRENT_TIMESTAMP text ("YYYY-MM-DD HH:MM:SS", UTC). */
export function parseSqliteUtc(value: string): Date | null {
  const match =
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }

  const [, yearText, monthText, dayText, hourText, minuteText, secondText] =
    match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);
  const parsed = new Date(0);
  parsed.setUTCFullYear(year, month - 1, day);
  parsed.setUTCHours(hour, minute, second, 0);

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day ||
    parsed.getUTCHours() !== hour ||
    parsed.getUTCMinutes() !== minute ||
    parsed.getUTCSeconds() !== second
  ) {
    return null;
  }
  return parsed;
}

function zonedParts(date: Date, timeZone: string, withTime: boolean) {
  const options: Intl.DateTimeFormatOptions = {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  if (withTime) {
    options.hour = "2-digit";
    options.minute = "2-digit";
    options.hourCycle = "h23";
  }
  const parts: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat("en-US", options).formatToParts(date)) {
    parts[part.type] = part.value;
  }
  return parts;
}

export function formatSqliteUtcMonthDay(
  value: string,
  timeZone = "Asia/Shanghai",
): string {
  const parsed = parseSqliteUtc(value);
  if (!parsed) {
    return "";
  }
  const parts = zonedParts(parsed, timeZone, false);
  if (!parts.month || !parts.day) {
    return "";
  }
  return `${parts.month}-${parts.day}`;
}

/** Format a SQLite UTC timestamp as "YYYY-MM-DD HH:mm" in the given zone. */
export function formatSqliteUtcDateTime(
  value: string,
  timeZone = "Asia/Shanghai",
): string {
  const parsed = parseSqliteUtc(value);
  if (!parsed) {
    return "";
  }
  const parts = zonedParts(parsed, timeZone, true);
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}

/** Format an ISO 8601 timestamp with offset ("2026-09-28T03:00:00+08:00") in the given zone. */
export function formatIsoDateTime(
  value: string,
  timeZone = "Asia/Shanghai",
): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  const parts = zonedParts(parsed, timeZone, true);
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}

/** Whole seconds between two SQLite UTC timestamps; null when either is unparseable. */
export function sqliteUtcSecondsBetween(start: string, end: string): number | null {
  const from = parseSqliteUtc(start);
  const to = parseSqliteUtc(end);
  if (!from || !to) {
    return null;
  }
  return Math.max(0, Math.round((to.getTime() - from.getTime()) / 1000));
}
